import {AlertButton, AlertOptions} from "@ionic/react";
import {HookOverlayOptions} from "@ionic/react/dist/types/hooks/HookOverlayOptions";
import {getManager} from "./Manager";
import {
    dispatchAllComponentsRendered,
    dispatchAnimationBlocksEnded, dispatchRedirectComplete,
    dispatchRefreshUI,
    dispatchUpdateBaseState
} from "../event/DispatchEvent";
import eventBus from "../event/EventBus";
import {Component} from "react";
import {SafeArea} from "capacitor-plugin-safe-area";
import RouteChoice from "../model-factory/basic/route/RouteChoice";
import {getModelFactory} from "../model-factory/ModelFactory";
import {INavButtonProps} from "../../tsx/element/button/IButton";
import {logOut} from "ionicons/icons";
import {BoundaryVO, PointVO} from "../common/Geometry";

export default class UiManager
{
    ionAlert: {
        (message: string, buttons?: AlertButton[] | undefined): Promise<void>;
        (options: AlertOptions & HookOverlayOptions): Promise<void>
    }|null = null;

    pauseForAnimation:boolean = false
    primaryViewDidChange:boolean = false;
    processingUiStateChange:boolean = false;
    renderingComponents:Component[] = [];
    animationBlocks:{}[] = [];
    requestedFindRouteViewPath:string|null = null;
    handlingPrimaryViewChange:boolean = false;
    handlingPageChange:boolean = false;
    findRouteViewTimeout:any = null;
    isImmediateRedirect:boolean = false;
    preheaderOffset: number = 40;
    useInfiniteScroll: boolean = true;
    scrollMilliseconds = 300;
    scrollOnPageChange:boolean = false;
    considerBodyPadding:boolean = true;
    scrollInterrupt = {
        canInterrupt: false,
        mayInterrupt: false,
        didInterrupt: false,
        minY:0,
        maxY:0,
    }

    constructor()
    {
        this.onNavigationRouteChangeStart = this.onNavigationRouteChangeStart.bind(this);
        this.onMouseWheel = this.onMouseWheel.bind(this);
        this.onNavigationRouteUpdate = this.onNavigationRouteUpdate.bind(this);
        this.findRouteView = this.findRouteView.bind(this);
        this.onUiStateChange = this.onUiStateChange.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);
        this.addEvents();
        this.applySaveArea()
    }

    showAlert(options:AlertOptions): Promise<void>
    {
        return this.ionAlert!(options)
    }

    handlePageTransition():Promise<void>
    {
        return this.scrollTo(0, 0, this.primaryViewDidChange && !this.scrollOnPageChange ? 0 : null);
    }

    addEvents(){

        eventBus.on("site.navigation-route-change-start", this.onNavigationRouteChangeStart);
        eventBus.on("site.navigation-route-update", this.onNavigationRouteUpdate);

        eventBus.on("site.fetch-request-state-change", this.onUiStateChange);
        eventBus.on("site.route-model-update", this.onUiStateChange);
        eventBus.on("site.data-model-update", this.onUiStateChange);
        eventBus.on("site.on-user-log-in-out", this.onUiStateChange);
        eventBus.on("site.user-location-update", this.onUiStateChange);
        eventBus.on("site.all-components-rendered", this.onUiStateChange);
        eventBus.on("site.animation-blocks-ended", this.onUiStateChange);
        window.onscroll = this.onWindowScroll;
        window.addEventListener("wheel", this.onMouseWheel);
    }

    onUiStateChange()
    {

        if(this.processingUiStateChange)
        {
            return
        }

        this.processingUiStateChange = true;

        setTimeout(() => {
            this.processingUiStateChange = false;
            dispatchRefreshUI();
        },1)
    }


    onWindowScroll()
    {
        if(window.scrollY > this.preheaderOffset){
            document.documentElement.classList.add("sticky-nav");
        } else {
            document.documentElement.classList.remove("sticky-nav");
        }
    }

    isUiDetermined()
    {
        return !getManager().navigation.routeIsProcessing &&
            !getManager().api.hasOpenFetchRequest() &&
            !getManager().location.isDeterminingPermissionStatus() &&
            !this.isRenderingComponents();
    }

    addAnimationBlock(milliseconds:number)
    {
        const block  = {};
        this.animationBlocks.push(block);
        setTimeout(()=>{
            const index = this.animationBlocks.indexOf(block);
            if(index >= 0){
                this.animationBlocks.splice(index,1);
                if(!this.animationBlocks.length){
                    dispatchAnimationBlocksEnded();
                }
            }

        }, milliseconds)
    }

    hasAnimationBlock()
    {
        return !!this.animationBlocks.length;
    }

    findRouteView()
    {
        let checkForAdditionalChange = false;
        eventBus.remove("site.refresh-ui", this.findRouteView);

        const onScrollEnd = () => {
            if(checkForAdditionalChange){
                if(!this.isUiDetermined() || this.hasAnimationBlock()){
                    eventBus.on("site.refresh-ui", this.findRouteView);
                } else {
                    this.findRouteView();
                }
            } else {
                this.scrollInterrupt.canInterrupt = false
                this.scrollInterrupt.didInterrupt = false
                this.requestedFindRouteViewPath = null;
                this.handlingPageChange = false;
                this.handlingPrimaryViewChange = false;
                this.removeMinimumMainContentHeight();
                dispatchRefreshUI();
            }
        }

        const applyFindRouteView = () => {
            this.findRouteViewTimeout = null;
            checkForAdditionalChange = !this.isUiDetermined() || this.hasAnimationBlock();

            const levelList = getManager().navigation.model.levelList;
            let willScroll = false;
            let requestedPathFound = false;
            if(!this.scrollInterrupt.didInterrupt){

                for(let i = levelList.length - 1; i >= 0; i--){
                    if(requestedPathFound && !this.handlingPageChange){
                        break;
                    }
                    const choice = levelList[i].choice;
                    if(!choice){
                        continue
                    }
                    const isRequestedPath =  !!this.requestedFindRouteViewPath && (choice.realPath === this.requestedFindRouteViewPath);
                    let selector = "[data-route-view='"+choice.realPath+"']";
                    let additionalSelector = "";
                    if(isRequestedPath){
                        requestedPathFound = true;
                    } else if(!this.handlingPageChange) {
                        continue;
                    } else {
                        additionalSelector = ".find-on-route-change";
                    }
                    if(!getManager().view.isSecondaryChoiceRoute(choice)){
                        break;
                    }

                    let fullSelector = additionalSelector+selector;

                    const bottomBoundary = this.getGlobalBoundary(fullSelector)

                    if(!bottomBoundary)
                    {
                        continue
                    }

                    willScroll = this.scrollToView(fullSelector, selector, true, onScrollEnd);

                    break;
                }
            }
            if(!willScroll){
                onScrollEnd();
            }
        }
        if(getManager().navigation.routeIsProcessing){
            return
        }
        if(this.findRouteViewTimeout){
            clearTimeout(this.findRouteViewTimeout );
        }
        const animationDelay = 300;

        this.findRouteViewTimeout = setTimeout(applyFindRouteView, animationDelay)
    }

    scrollToView(selector: string, parentSelector: string|null = null, scrollToTop:boolean = false, onScrollEnd: (() => void )|null = null, milliseconds?: number|null){

        const bottomBoundary = this.getGlobalBoundary(selector)

        if(!bottomBoundary)
        {
            return false;
        }

        const ionScrollElement = this.getIonScrollElement(selector);
        const scrollElement = this.getScrollElement(ionScrollElement, selector);
        const scrollElementScrollHeight = scrollElement?.scrollHeight ?? 0;
        const scrollElementInnerHeight = scrollElement?.clientHeight ?? 0;

        if(scrollElementScrollHeight <= scrollElementInnerHeight)
        {
            return false;
        }
        const scrollTop = scrollElement?.scrollTop ?? 0;

        parentSelector = parentSelector ?? selector;
        let topBoundary = this.getGlobalBoundary(parentSelector, this.considerBodyPadding);
        topBoundary = topBoundary ? topBoundary : bottomBoundary!;
        const boundaryTop = topBoundary.top;
        const boundaryBottom = bottomBoundary.bottom;

        let targetY:number|null = null;
        if(scrollToTop && (boundaryTop < scrollTop)){
            targetY = boundaryTop;
        } else if(boundaryBottom > (scrollTop + scrollElementInnerHeight)){
            targetY = Math.min(boundaryBottom - scrollElementInnerHeight, boundaryTop);
        } else if(boundaryTop < scrollTop){
            targetY = boundaryTop;
        }
        if(targetY === null)
        {
            return false;
        }

        this.scrollElementTo(scrollElement!, ionScrollElement, null, targetY, milliseconds).then(onScrollEnd);
        return true;
    }


    onNavigationRouteChangeStart()
    {
        this.scrollInterrupt.canInterrupt = true;
        this.scrollInterrupt.mayInterrupt = false;
        this.scrollInterrupt.didInterrupt = false;
    }

    onMouseWheel()
    {
        if(this.scrollInterrupt.canInterrupt && !this.scrollInterrupt.didInterrupt){
            let ionScrollElement = this.getIonScrollElement();
            const scrollElement = this.getScrollElement(ionScrollElement);
            if(scrollElement){

                if(!this.scrollInterrupt.mayInterrupt){
                    this.scrollInterrupt.mayInterrupt = true;
                    this.scrollInterrupt.minY = scrollElement.scrollTop;
                    this.scrollInterrupt.maxY = scrollElement.scrollTop;
                } else {
                    if((Math.abs(scrollElement.scrollTop - this.scrollInterrupt.minY) > 100) || Math.abs(scrollElement.scrollTop - this.scrollInterrupt.maxY) > 100){
                        this.scrollInterrupt.didInterrupt = true;
                    } else {
                        this.scrollInterrupt.minY = Math.min(this.scrollInterrupt.minY, scrollElement.scrollTop);
                        this.scrollInterrupt.maxY = Math.max(this.scrollInterrupt.maxY, scrollElement.scrollTop);
                    }
                }
            }
        }
    }

    onNavigationRouteUpdate()
    {
        this.handlePageChangeStarted();
        this.onUiStateChange();
        this.applyMinimumMainContentHeight();
    }

    handlePageChangeStarted()
    {
        this.requestedFindRouteViewPath = null;
        this.handlingPageChange = true;
        if(this.primaryViewDidChange){
            this.handlingPrimaryViewChange = true;
        }

        this.findRouteView();
        dispatchRedirectComplete();
    }

    findRouteViewChoice(realPath:string)
    {
        this.requestedFindRouteViewPath = realPath;
        this.findRouteView();
    }

    getIonScrollElement(withChild?:HTMLElement|string|null):HTMLIonContentElement|null
    {
        const scrollElement = this.getHtmlElement("ion-content.primary-content", withChild);
        if(!scrollElement){
            return null;
        }
        const ionScrollElement:HTMLIonContentElement = scrollElement as HTMLIonContentElement;
        const innerElement = this.getIonInnerScrollElement(ionScrollElement);
        return innerElement ? ionScrollElement : null;
    }

    getIonInnerScrollElement(ionScrollElement:HTMLIonContentElement):HTMLElement|null
    {
        let element = ionScrollElement.shadowRoot?.querySelector(".inner-scroll") ?? null;
        return element instanceof HTMLElement ? element : null;
    }

    getScrollElement(ionScrollElement?:HTMLIonContentElement|null, withChild?:HTMLElement|string|null):HTMLElement|null
    {
        if(typeof withChild === 'string'){
            withChild = this.getHtmlElement(withChild);
            if(!withChild){
                return null;
            }
        }
        const scrollElement = ionScrollElement ? this.getIonInnerScrollElement(ionScrollElement) : null;
        if(scrollElement){
            if(!withChild || scrollElement.contains(scrollElement)){
                return scrollElement;
            }
        }
        if(withChild){
            return this.getParentScroll(withChild);
        }

        return document.documentElement
    }

    getGlobalBoundary( selector:string, considerBodyPadding:boolean = false ):BoundaryVO|null {
        const element = this.getHtmlElement(selector);
        if(!element){
            return null;
        }
        let outerScroll = this.getHtmlElement("ion-content[scroll-events]", element);
        outerScroll = outerScroll ? outerScroll : this.getParentScroll(element);

        const offset = this.getElementOffset(element, outerScroll, considerBodyPadding);
        if(!offset){
            return null;
        }
        return new BoundaryVO(offset.x, offset.x + element.offsetWidth, offset.y, offset.y + element.offsetHeight);
    }

    getParentScroll(ofElement:HTMLElement): HTMLElement|null
    {
        let next = ofElement.parentElement instanceof HTMLElement ? ofElement.parentElement : null;
        while(next){
            if(next.scrollHeight > next.clientHeight){
                return  next;
            }
            next = next.parentElement instanceof HTMLElement ? next.parentElement : null;
        }
        return null;
    }

    getHtmlElement( selector:string, withChild?:HTMLElement|string|null ):HTMLElement|null {
        const elements = document.querySelectorAll(selector);
        if(typeof withChild === 'string'){
            withChild = this.getHtmlElement(withChild);
            if(!withChild){
                return null;
            }
        }
        for(let element of elements) {
            const htmlElement = element instanceof HTMLElement ? element : (element?.parentElement ?? null);
            if(!htmlElement){
                continue;
            }
            if(withChild)
            {
                if(!htmlElement.contains(withChild))
                {
                    continue;
                }
            }
            return htmlElement;
        }
        return null;
    }

    getElementOffset( ofElement:HTMLElement|null, toElement?:HTMLElement|null, considerBodyPadding:boolean = false ):PointVO|null {
        const elements:HTMLElement[] = [];
        let foundInParent = false;
        let element:HTMLElement|null = ofElement;
        let x = 0;
        let y = 0;
        while( element ) {
            if(toElement){
                if(toElement === element){
                    foundInParent = true;
                    break;
                }
            } else if(element?.nodeName === "HTML"){
                foundInParent = true;
                break
            }
            elements.push(element);
            element = element.offsetParent && element.offsetParent instanceof HTMLElement ? element.offsetParent : element.parentElement;
            if(considerBodyPadding){
                if(element instanceof HTMLBodyElement){
                    y -= parseFloat(window.getComputedStyle(element, null).getPropertyValue('padding-top'));
                }
            }
        }
        if(!foundInParent){
            return null;
        }

        elements.reverse();
        for(let element of elements){
            x += element.offsetLeft - element.scrollLeft;
            y += element.offsetTop - element.scrollTop;
        }
        return new PointVO(x, y);
    }

    scrollTo = (x?:number|null, y?:number|null, milliseconds?: number|null):Promise<void> => {
        let ionScrollElement = this.getIonScrollElement();
        const scrollElement = this.getScrollElement(ionScrollElement);
        if(!scrollElement){
            return new Promise( (resolve, reject) => {
                reject(new Error("Could not find scroll element!"));
            })
        }
        return this.scrollElementTo(scrollElement, ionScrollElement, x, y, milliseconds);
    }

    scrollElementTo = (scrollElement:HTMLElement, ionScrollElement?:HTMLIonContentElement|null, x?:number|null, y?:number|null, milliseconds?: number|null):Promise<void> => {

        const minimumScrollPX = 10;

        return new Promise((resolve) => {

            x = x ?? null;
            y = y ?? null;
            let doScrollX = false;
            let doScrollY = false;
            if(x !== null){
                x = Math.max(Math.min(x, scrollElement.scrollWidth - scrollElement.clientWidth), 0)
                const xDist = Math.abs(scrollElement.scrollLeft - x);
                if(xDist >= minimumScrollPX){
                    doScrollX = true;
                }
            }
            if(y !== null){
                y = Math.max(Math.min(y, scrollElement.scrollHeight - scrollElement.clientHeight), 0)
                const yDist = Math.abs(scrollElement.scrollTop - y);
                if(yDist >= minimumScrollPX){
                    doScrollY = true;
                }
            }
            if(!doScrollX && !doScrollY){
                resolve();
                return;
            }

            const onComplete = () => {
                getManager().ui.pauseForAnimation = false;
                dispatchUpdateBaseState();
                resolve();
            }

            getManager().ui.pauseForAnimation = true;

            milliseconds = milliseconds ?? this.scrollMilliseconds;
            if(ionScrollElement){
                ionScrollElement.scrollToPoint(x ?? undefined, y  ?? undefined, milliseconds).then(onComplete).catch(onComplete);
            } else {
                this.scrollNativeElementTo(scrollElement, x, y, !milliseconds ? "instant" : null).then(onComplete).catch(onComplete);
            }
            dispatchUpdateBaseState();
        })
    }

    scrollNativeElementTo = (element:HTMLElement, x?:number|null, y?:number|null, behavior?:ScrollBehavior|null):Promise<void> => {
        return new Promise((resolve) => {

            x = x ?? null;
            y = y ?? null;
            behavior = behavior ?? "smooth";
            if(x === null && y === null){
                resolve();
                return;
            }
            const options:ScrollToOptions = {};
            if(x !== null){
                options.left = x!;
            }
            if(y !== null){
                options.top = y!;
            }
            if(behavior !== null){
                options.behavior = behavior!;
            }

            let lastX = element.scrollLeft;
            let lastY = element.scrollTop;

            const intervalID = setInterval(() => {
                if(element.scrollLeft == lastX && element.scrollTop == lastY){
                    onComplete();
                    return;
                }

                lastX = element.scrollLeft;
                lastY = element.scrollTop;

            }, 200);

            const onComplete = () =>{
                element.removeEventListener('scroll', onChange);
                clearInterval(intervalID);
                resolve();
            }

            const onChange = () => {
                if((x === null || (element.scrollLeft === x)) && y === null || (element.scrollTop === y)){
                    onComplete();
                }
            }


            element.addEventListener('scroll', onChange);
            onChange();
            element.scrollTo(options)
        })

    }

    isRenderingComponents():boolean
    {
        return !!this.renderingComponents.length;
    }

    addRenderingComponent(component:Component):void
    {
        if(!this.renderingComponents.includes(component)){
            this.renderingComponents.push(component);
        }
    }

    removeRenderingComponent(component:Component){
        if(!this.renderingComponents.length){
            return
        }
        while(true){
            const index = this.renderingComponents.indexOf(component);
            if(index < 0){
                break;
            }
            this.renderingComponents.splice(index, 1);
        }
        if(this.renderingComponents.length){
            return;
        }
        dispatchAllComponentsRendered();
    }

    applyMinimumMainContentHeight()
    {
        if(this.primaryViewDidChange){
            this.removeMinimumMainContentHeight();
            return;
        }
        const mainContent = document.getElementById("mainContent");
        if(!mainContent){
            return;
        }
        const minHeight = mainContent.style.minHeight;
        if(minHeight){
            return;
        }
        const currentHeight = mainContent.clientHeight;
        mainContent.style.minHeight = currentHeight+"px";
    }

    removeMinimumMainContentHeight()
    {
        const mainContent = document.getElementById("mainContent");
        if(!mainContent){
            return;
        }
        mainContent.style.minHeight = "";
    }

    getUtilityNavChoiceList():(RouteChoice|INavButtonProps)[]
    {
        const choiceList:(RouteChoice|INavButtonProps)[] = [];
        if(getManager().user.loggedInLocal){
            const myProfileChoice = getModelFactory().browseRoute.getDefaultModel().choices.my_profile ?? null;
            if(myProfileChoice){
                choiceList.push(myProfileChoice);
            }

            choiceList.push({
                onClick: () => getManager().user.logOut(),
                label: "Logout",
                href: "/#"
            })

        } else {
            const registerChoice = (getModelFactory().accountRoute.getDefaultModel().choices.register as RouteChoice) ?? null;
            const loginChoice = (getModelFactory().accountRoute.getDefaultModel().choices.login as RouteChoice) ?? null;
            if(registerChoice){
                choiceList.push(registerChoice);
            }
            if(loginChoice){
                choiceList.push(loginChoice);
            }
        }

        return choiceList;
    }

    applySaveArea(){
        SafeArea.getSafeAreaInsets().then((data) => {
            const { insets } = data;
            document.body.style.setProperty("--ion-safe-area-top", insets.top + "px");
            document.body.style.setProperty(
                "--ion-safe-area-right",
                insets.right + "px"
            );
            document.body.style.setProperty(
                "--ion-safe-area-bottom",
                insets.bottom + "px"
            );
            document.body.style.setProperty(
                "--ion-safe-area-left",
                insets.left + "px"
            );
        });
    }

    removeFocus(){
        const focusElements = document.querySelectorAll(":focus");
        for(let element of focusElements){
            const htmlElement = element instanceof HTMLElement ? element as HTMLElement : null;
            if(htmlElement){
                htmlElement.focus();
                htmlElement.blur();
            }
        }
    }
}